/**
 * MapNameExtractor - extracts map names from asset paths.
 */

import type { AssetPath } from '../storage/storageProvider.js';

/** Extracts the map name from an asset path.
 *
 * @param path - The asset path
 * @returns The map relative path without @project/ prefix and without .genesys-scene extension, or null if unable to extract
 *
 * @example
 * - '@project/assets/server.genesys-scene' -> 'assets/server'
 * - '@project/server.genesys-scene' -> 'server'
 * - '@engine/demos/examples/demo.genesys-scene' -> 'demos/examples/demo'
 * - '@project\assets\server.genesys-scene' -> 'assets/server' (Windows paths normalized)
 */
export function extractMapNameFromPath(path: AssetPath | undefined): string | null {
  if (!path) {
    return null;
  }
  // Normalize backslashes to forward slashes for Windows compatibility
  const pathStr = path.initialPath.replace(/\\/g, '/');

  // Match paths like '@project/assets/server.genesys-scene' or '@engine/demos/demo.genesys-scene'
  // Capture everything after the prefix and before .genesys-scene
  const match = pathStr.match(/^@(?:project|engine)\/(.+)\.genesys-scene$/);

  if (!match) {
    console.error(`[extractMapNameFromPath] Failed to extract map name from path: "${pathStr}" (original: "${path.initialPath}")`);
    console.error('[extractMapNameFromPath] Expected format: @project/path/to/map.genesys-scene or @engine/path/to/map.genesys-scene');
    return null;
  }

  return match[1];
}

